// src/components/results/mapLCW.js
import React, {
  useMemo, useState, useCallback, useContext, useEffect, useRef
} from "react";
import { GoogleMap, Marker, InfoWindow, useLoadScript } from "@react-google-maps/api";
import AuthContext from "../../Context/AuthContext";

export default function MobilityMap({
  rawData = [],
  centerFallback = { lat: 41.04, lng: 28.99 },
  zoom = 12,
}) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyCaHzltTBFsM9LOb9EDlMHdUV-m1Od0hF4",
  });
  const { userData } = useContext(AuthContext);

  const sestoColor = {
    "A": "red",
    "B": "blue",
    "C": "purple",
    "Mağaza": "pink",
  };

  const STORE_SVG_DATA_URL =
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">
        <path fill="#555" d="M3 9h18v13H3z"/>
        <path fill="#777" d="M2 8l2-4h16l2 4z"/>
        <rect x="8" y="13" width="8" height="9" fill="#999"/>
        <rect x="10" y="15" width="4" height="4" fill="#555"/>
      </svg>
    `);

  // ---- Noktalar
  const points = useMemo(() => {
    const norm = (v) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : v) || null;
    return (Array.isArray(rawData) ? rawData : [])
      .map((r, idx) => {
        const lat = Number(String(r.latitude).replace(",", "."));
        const lng = Number(String(r.longitude).replace(",", "."));
        const type = norm(r["Liste Tür"] ?? r["Liste  Tür"] ?? null);
        const ses = norm(r["SES"]) || (type === "Mağaza" ? "Mağaza" : null);
        const colorName = (ses && sestoColor[ses]) || "red";
        return {
          id: `p${idx + 1}`,
          lat: Number.isFinite(lat) ? lat : null,
          lng: Number.isFinite(lng) ? lng : null,
          title: r["Adres"] || type || `Konum #${idx + 1}`,
          address: r["Adres"] || null,
          type,
          ses,
          colorName,
          raw: r,
        };
      })
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  }, [rawData]);

  // ---- Başlangıç merkezi
  const initialCenter = useMemo(() => {
    if (!points.length) return centerFallback;
    const sum = points.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
    return { lat: sum.lat / points.length, lng: sum.lng / points.length };
  }, [points, centerFallback]);

  // ---- Controlled center: sadece ilk kez set et
  const [mapCenter, setMapCenter] = useState(initialCenter);
  const [didCenterOnce, setDidCenterOnce] = useState(false);
  const mapRef = useRef(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!didCenterOnce) {
      setMapCenter(initialCenter);
      setDidCenterOnce(true);
    }
  }, [initialCenter, didCenterOnce]);

  const onLoad = useCallback((map) => (mapRef.current = map), []);
  const onDragEnd = useCallback(() => {
    if (mapRef.current) setMapCenter(mapRef.current.getCenter().toJSON());
  }, []);

  // ---- Legend + Filtre (SES bazlı)
  const allSES = useMemo(
    () => Array.from(new Set(points.map((p) => p.ses).filter(Boolean))).sort(),
    [points]
  );
  const [visibleSES, setVisibleSES] = useState(() => new Set(allSES)); // hepsi açık başla

  // allSES değişirse (data güncellenirse) görünürlüğü senkronla
  useEffect(() => {
    setVisibleSES((prev) => {
      const next = new Set(prev);
      allSES.forEach((t) => next.add(t));
      // var olmayanları çıkar
      for (const t of Array.from(next)) if (!allSES.includes(t)) next.delete(t);
      return next;
    });
  }, [allSES]);

  const toggleSES = (t) =>
    setVisibleSES((s) => {
      const n = new Set(s);
      n.has(t) ? n.delete(t) : n.add(t);
      return n;
    });

  const setAll = (on) => setVisibleSES(new Set(on ? allSES : []));

  const legendItems = useMemo(
    () =>
      allSES.map((label) => {
        const color = sestoColor[label] || "red";
        const iconSrc =
          label === "Mağaza"
            ? STORE_SVG_DATA_URL
            : `https://maps.google.com/mapfiles/ms/icons/${color}-dot.png`;
        return { label, iconSrc, checked: visibleSES.has(label) };
      }),
    [allSES, visibleSES]
  );

  if (loadError) return <div style={{ padding: 12, color: "#b00020" }}>Harita yüklenirken hata: {String(loadError.message || loadError)}</div>;
  if (!isLoaded) return <div>Harita yükleniyor…</div>;

  // ---- InfoWindow stilleri
  const cardStyle = { maxWidth: 260, display: "grid", gap: 8, fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif" };
  const titleStyle = { margin: 0, fontSize: 16, fontWeight: 600 };
  const metaStyle = { margin: 0, fontSize: 13, lineHeight: 1.4, opacity: 0.9 };
  const infoOpts = { maxWidth: 280, pixelOffset: new window.google.maps.Size(0, -8) };

  const getMarkerIcon = (colorName, ses) =>
    ses === "Mağaza"
      ? { url: STORE_SVG_DATA_URL, scaledSize: new window.google.maps.Size(28, 28), anchor: new window.google.maps.Point(14, 28) }
      : `https://maps.google.com/mapfiles/ms/icons/${colorName}-dot.png`;

  // ---- Filtre uygulanmış noktalar
  const visiblePoints = points.filter((p) => !p.ses || visibleSES.has(p.ses));

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      <div
        style={{
          height: "800px",
          width: "80%",
          margin: "40px auto",
          borderRadius: 8,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.2)",
          position: "relative",
        }}
      >
        <GoogleMap
          center={mapCenter}
          zoom={zoom}
          onLoad={onLoad}
          onDragEnd={onDragEnd}
          mapContainerStyle={{ height: "100%", width: "100%" }}
          options={{ gestureHandling: "greedy" }}
        >
          {visiblePoints.map((p) => (
            <Marker
              key={p.id}
              position={{ lat: p.lat, lng: p.lng }}
              onClick={() => setActive(p)}
              icon={getMarkerIcon(p.colorName, p.ses)}
            />
          ))}

          {active && (
            <InfoWindow position={{ lat: active.lat, lng: active.lng }} onCloseClick={() => setActive(null)} options={infoOpts}>
              <div style={cardStyle}>
                <h3 style={titleStyle}>{active.title}</h3>
                {active.address && (
                  <p style={metaStyle}>
                    <strong>Adres:</strong> {active.address}
                  </p>
                )}
                {active.type && (
                  <p style={metaStyle}>
                    <strong>Liste Tür:</strong> {active.type}
                  </p>
                )}
                {active.ses && (
                  <p style={metaStyle}>
                    <strong>SES:</strong> {active.ses}
                  </p>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Legend + Filtre */}
        <div
          style={{
            position: "absolute",
            left: 12,
            bottom: 12,
            background: "rgba(255,255,255,.95)",
            padding: "10px 12px",
            borderRadius: 8,
            boxShadow: "0 1px 6px rgba(0,0,0,.2)",
            fontSize: 13,
            minWidth: 210,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            {/* <div style={{ fontWeight: 600 }}>Katman Renkleri</div> */}
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setAll(true)} style={{ fontSize: 12, padding: "2px 6px", border: "1px solid #ddd", borderRadius: 6, background: "#fff" }}>
                Tümünü Aç
              </button>
              <button onClick={() => setAll(false)} style={{ fontSize: 12, padding: "2px 6px", border: "1px solid #ddd", borderRadius: 6, background: "#fff" }}>
                Kapat
              </button>
            </div>
          </div>

          {legendItems.map((it) => (
            <label key={it.label} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={it.checked}
                onChange={() => toggleSES(it.label)}
                style={{ margin: 0 }}
              />
              <img alt={it.label} width={18} height={18} src={it.iconSrc} />
              <span>{it.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
